import React, { useEffect, useState } from 'react'

import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogBody,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogFooter,
  Divider,
  Text,
  Button,
  ButtonProps,
  Spacer,
  Box,
} from '@chakra-ui/react'
import { faCheck, IconDefinition } from '@fortawesome/pro-regular-svg-icons'

import Icon from 'src/components/widgets/Icon/Icon'

type SubmitPhase = 'init' | 'loading' | 'submitSuccess' | 'resubmit'

type ButtonLabels = {
  close?: string
  submit?: string
  loading?: string
  submitSuccess?: string
  resubmit?: string
}

const getSubmitLabel = (labels: ButtonLabels, phase: SubmitPhase) => {
  switch (phase) {
    case 'init':
      return labels?.submit || 'Submit'
    case 'loading':
      return labels?.loading
    case 'submitSuccess':
      return labels?.submitSuccess || 'Done'
    case 'resubmit':
      return labels?.resubmit || 'Submit'
  }
}

const DialogModal: React.FC<{
  isOpen: boolean
  onClose(): void
  icon?: IconDefinition
  title?: string
  text?: string
  buttonLabels?: Partial<ButtonLabels>
  hideCloseButton?: boolean
  submitButtonProps?: ButtonProps
  delayResubmitMs?: number
  autoSubmitOnOpen?: boolean
  closeAfterSubmit?: boolean
  onSubmit?: () => void | Promise<void>
}> = ({
  isOpen,
  icon,
  title,
  text,
  buttonLabels,
  hideCloseButton,
  delayResubmitMs = 0,
  submitButtonProps,
  autoSubmitOnOpen = false,
  closeAfterSubmit = true,
  onSubmit,
  onClose,
}) => {
  const cancelRef = React.useRef()
  const [submitPhase, setSubmitPhase] = useState<SubmitPhase>('init')
  const currentSubmitLabel = getSubmitLabel(buttonLabels, submitPhase)

  const submitHandler: React.MouseEventHandler = async (ev) => {
    setSubmitPhase('loading')
    !!onSubmit && (await onSubmit())
    if (delayResubmitMs) {
      setSubmitPhase('submitSuccess')
    } else {
      setSubmitPhase('init')
    }
    closeAfterSubmit && closeHandler(ev)
  }

  useEffect(() => {
    const id = setTimeout(() => {
      if (submitPhase === 'submitSuccess') {
        setSubmitPhase('resubmit')
      }
    }, delayResubmitMs)

    return () => {
      clearTimeout(id)
    }
  }, [submitPhase, delayResubmitMs])

  const closeHandler: React.MouseEventHandler = (ev) => {
    ev.stopPropagation()
    ev.preventDefault()

    if (submitPhase != 'init') {
      setSubmitPhase('init')
    }

    onClose()
  }

  useEffect(() => {
    autoSubmitOnOpen && isOpen && submitHandler(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  if (submitButtonProps?.bg && !submitButtonProps._hover) {
    submitButtonProps._hover = {
      bg: submitButtonProps.bg,
      boxShadow: 'sm',
    }
  }

  return (
    <AlertDialog
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      leastDestructiveRef={cancelRef}
    >
      <AlertDialogOverlay>
        <AlertDialogContent maxW={{ base: 343, md: 450 }} borderRadius={24}>
          {!!(title || icon) && (
            <AlertDialogHeader
              fontSize="lg"
              fontWeight="bold"
              pb={4}
              pt={6}
              px={6}
            >
              {!!icon && (
                <Icon
                  icon={icon}
                  color="onyx"
                  fontSize="20px"
                  paddingBottom={4}
                />
              )}
              {!!title && (
                <Text as="h5" className="thick" fontWeight="bold">
                  {title}
                </Text>
              )}
            </AlertDialogHeader>
          )}

          {!!text && (
            <AlertDialogBody px={6} pb={6} className="body-small" color="stone">
              {text}
            </AlertDialogBody>
          )}
          <Divider color="sandstone" />
          <AlertDialogFooter py={4}>
            {!hideCloseButton && (
              <>
                <Button
                  onClick={closeHandler}
                  variant="text"
                  px={3}
                  paddingX={3}
                  data-testid="modal-closeButton"
                  ref={cancelRef}
                >
                  {buttonLabels?.close}
                </Button>
                <Spacer />
              </>
            )}
            <Button
              w="auto"
              h="48px"
              data-testid="modal-okButton"
              isLoading={submitPhase === 'loading'}
              loadingText={submitPhase === 'loading' && currentSubmitLabel}
              isDisabled={
                submitPhase === 'loading' || submitPhase === 'submitSuccess'
              }
              _disabled={{
                backgroundColor: 'air',
                borderColor: 'air',
                boxShadow: 'none',
                color: 'graphite',
                _hover: {
                  backgroundColor: 'air',
                  borderColor: 'air',
                },
              }}
              {...submitButtonProps}
              rightIcon={
                submitPhase != 'submitSuccess' && submitButtonProps?.rightIcon
              }
              leftIcon={
                submitPhase === 'submitSuccess' && (
                  <Icon icon={faCheck} color="green.200" />
                )
              }
              onClick={submitHandler}
            >
              <Box mx={1}>{currentSubmitLabel || 'Ok'}</Box>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}

export default DialogModal
